import { isRole } from '@sbg/shared/constants';
import type { UserDto } from '@sbg/shared/schemas';
import { apiFetch, getCsrfToken, isAbortError } from '@/lib/api/client';
import { readStorage, removeStorage, writeStorage } from '@/lib/storage';
import { useAuthStore } from './store';

/**
 * A non-secret "a session probably exists" marker (the real cookies are httpOnly and invisible to
 * JS). It only decides whether a 401 from `/auth/me` is worth a silent refresh attempt, so anonymous
 * visitors never trigger a pointless `POST /auth/refresh` on every page load.
 */
export const SESSION_HINT_KEY = 'sbg.session';

export const hasSessionHint = (): boolean => readStorage(SESSION_HINT_KEY) === '1';

/** Set the signed-in user (or sign out) and keep the session hint in step. */
export function setSessionUser(user: UserDto | null): void {
  if (user) writeStorage(SESSION_HINT_KEY, '1');
  else removeStorage(SESSION_HINT_KEY);
  useAuthStore.getState().setUser(user);
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const looksLikeUser = (value: unknown): value is UserDto =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.email === 'string' &&
  isRole(value.role);

/**
 * `GET /auth/me` returns the user either bare or wrapped (`{ user, permissions, flags }`);
 * accept both so the client does not depend on that detail.
 */
export function extractUser(payload: unknown): UserDto | null {
  const candidate = isRecord(payload) && 'user' in payload ? payload.user : payload;
  return looksLikeUser(candidate) ? candidate : null;
}

/**
 * Can this browser keep the "a session exists" hint across page loads? (`localStorage` throws when site
 * data is blocked; the in-memory fallback of `lib/storage` only lasts for one page.)
 */
function hintCanPersist(): boolean {
  try {
    const key = `${SESSION_HINT_KEY}.probe`;
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Is there any reason to ask the server who is signed in? Only a browser that remembers having held a
 * session (the hint, written at every sign-in and registration) does. Anyone else — nearly every
 * visitor, and every anonymous *returning* one — skips the request: no wasted round trip and no `401`
 * in the console (Lighthouse "errors in console"). The CSRF cookie is no evidence either way: the API
 * sets it on every response, signed in or not (D-092, D-189).
 *
 * Two cases the hint cannot cover, handled elsewhere or here:
 *  · a session the API created behind the page's back — the Google redirect comes back to
 *    `/login?via=google`; `isGoogleReturn()` tells `loadSession` to ask regardless;
 *  · a browser that cannot persist the hint at all: with `localStorage` blocked it would forget its own
 *    sessions on every load, so for it the CSRF cookie still counts, as it used to.
 */
export const shouldProbeSession = (): boolean =>
  hasSessionHint() || (!hintCanPersist() && getCsrfToken() !== null);

/**
 * Has the person just come back from Google? The sign-in page is opened as `/login?via=google[&next=…]`
 * with the new session's cookies already set but no hint in this browser yet.
 */
export function isGoogleReturn(
  location: Pick<Location, 'pathname' | 'search'> = window.location,
): boolean {
  return (
    location.pathname.replace(/\/+$/, '') === '/login' &&
    new URLSearchParams(location.search).get('via') === 'google'
  );
}

export interface LoadSessionOptions {
  /** Ask the server even though this browser holds no hint (the way back from Google). */
  force?: boolean;
}

/**
 * Ask the server who is signed in (`GET /auth/me`). A 401 simply means "anonymous" and is ignored;
 * a silent refresh is tried first when the browser remembers a previous session (expired access
 * token, valid refresh cookie). Browsers with no trace of a session skip the request.
 */
export async function loadSession(
  signal?: AbortSignal,
  { force = false }: LoadSessionOptions = {},
): Promise<void> {
  if (!force && !shouldProbeSession()) {
    setSessionUser(null);
    return;
  }
  useAuthStore.getState().setStatus('loading');
  try {
    const payload = await apiFetch<unknown>('/auth/me', { signal, refreshOn401: hasSessionHint() });
    setSessionUser(extractUser(payload));
  } catch (error) {
    if (isAbortError(error)) return; // superseded (StrictMode re-run / unmount)
    setSessionUser(null); // 401 → anonymous; network/5xx → treated as signed out, never as an app crash
  }
}
